'use client';

/**
 * FavouritesExplorerCard — the buyer's "My Favourites" grouping (the SAVED
 * tier), as a standalone, self-fetching explorer card reusable across modes
 * (mode4 catalogue, mode11 search). Renders nothing until the caller has saved
 * something.
 *
 * A thin wrapper over the shared `ProductTierRow` primitive: this card owns the
 * DATA (self-fetches `saved_list`) and the BEHAVIOUR (row click → canvas
 * sel:favourites; leaf click → inspectEntity), and hands the shell its four
 * knobs — label, ♥ icon, the saved products, and the canvas target. The row /
 * chevron / inline-tree paint lives in the primitive, shared with the WANTS
 * sibling (custom_products_explorer). See product-tier-row.tsx for why SAVED and
 * WANTS are one shell.
 *
 * inspectEntity (NOT selectNode) on a leaf: shows the product's brief in the
 * rail while the favourites grid stays on canvas. selectNode is masked by
 * explorerSelection='favourites' (entityType would resolve to 'favourites', not
 * 'product') — same reason product_search_results uses inspectEntity.
 *
 * Why a PEER card and NOT nested under the buyer's own "… — Custom Products"
 * node: that node is the WANTS tier — bespoke BW_ wishes the buyer OWNS. A
 * favourite is the SAVED tier — a bookmark of another vendor's catalogue PT_.
 * SAVED and WANTS are sibling buyer→product edges, so favourites is a peer
 * tier, not a child of wishes.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="favourites_explorer"
 * @see ./product-tier-row.tsx — the shared shell
 * @see ./custom-products-explorer-card.tsx — the WANTS sibling
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useMemo } from 'react';
import { Heart } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { ProductTierRow, type ProductTierLeaf } from './product-tier-row';
import type { SavedProductRow } from '@/lib/api/types';

interface SavedListResponse {
  products: SavedProductRow[];
  count: number;
}

export function FavouritesExplorerCard() {
  const label = useProductLabel('favourites_group');
  const strategy = useCapabilityStore((s) => s.strategyName('products', 'saved_list'));
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const isActive = useLayoutStore((s) => s.explorerSelection) === 'favourites';

  const { data } = useStrategyData<SavedListResponse>(strategy, {}, !!strategy);
  const res = data?.data;
  const rows = res?.products ?? [];
  const count = res?.count ?? rows.length;

  // saved_list is eager — the full product list rides in the one response, so
  // the leaves are always populated (no lazy fetch, unlike the WANTS sibling).
  const leaves: ProductTierLeaf[] = useMemo(
    () => rows.map((r) => ({
      id: r.product_sid,
      name: r.name,
      list_price: r.list_price,
      image_url: r.image_url,
      // A delisted save greys out (tombstone) rather than disappearing.
      delisted: r.active === false || r.sale_ok === false,
    })),
    [rows],
  );

  const queryKey = useMemo(() => ['strategy-data', strategy, {}] as const, [strategy]);
  useCardExport(useMemo(() => ({
    label,
    content_key: 'favourites_explorer',
    surface: 'explorer' as const,
    status: (res ? 'loaded' : 'loading') as 'loading' | 'loaded',
    query_key: queryKey,
    text: rows.map((r) => r.name).join('\n'),
    json: {
      count,
      products: rows.map((r) => ({
        product_id: r.product_sid, name: r.name, vendor: r.vendor_name ?? null,
      })),
    },
  }), [rows, res, count, queryKey, label]));

  // No favourites → no grouping. The affordance only appears once earned.
  if (rows.length === 0) return null;

  return (
    <ProductTierRow
      label={label}
      icon={Heart}
      iconClassName="text-rose-400 fill-rose-400"
      count={count}
      products={leaves}
      isActive={isActive}
      onOpen={() => setExplorerSelection('favourites')}
      onLeafClick={(p) =>
        inspectEntity({
          id: p.id,
          label: p.name,
          type: 'product',
          properties: { list_price: p.list_price, image_url: p.image_url },
        })
      }
      activeLeafId={inspectedEntity?.id ?? null}
      leafTitle={(p) => (p.delisted ? `${p.name} — no longer available` : p.name)}
    />
  );
}
